import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import type { StrategyAction } from '@/types/collection-strategies';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useActionTypeOptions, useTemplatesByType } from '@/hooks/useStrategiesData';

interface StrategyStageEditorProps {
  actions: StrategyAction[];
  onActionsChange: (actions: StrategyAction[]) => void;
}

const smsTemplates = [
  'SMS - Payment Due',
  'SMS - Urgent Payment',
  'SMS - Payment Link',
];

export function StrategyStageEditor({ actions, onActionsChange }: StrategyStageEditorProps) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [, setErrors] = useState<Record<string, string>>({});
  // setErrors is available for future validation implementation
  void setErrors;

  // Lookup data for dropdowns (cached for 1 minute)
  const { data: actionTypeOptions = [] } = useActionTypeOptions();
  const { data: emailTemplateOptions = [] } = useTemplatesByType('email');

  const addStage = () => {
    if (actions.length >= 10) {
      toast.error('Maximum 10 stages reached');
      return;
    }

    const newAction: StrategyAction = {
      id: `action-${Date.now()}`,
      type: 'email',
      timing: {
        type: 'days_from_trigger',
        daysFromTrigger: 0,
      },
      templateName: '',
      order: actions.length + 1,
    };

    onActionsChange([...actions, newAction]);
  };

  const removeStage = (index: number) => {
    const updatedActions = actions.filter((_, i) => i !== index);
    // Renumber actions
    const renumberedActions = updatedActions.map((action, i) => ({
      ...action,
      order: i + 1,
    }));
    onActionsChange(renumberedActions);
  };

  const updateStage = (index: number, field: keyof StrategyAction, value: StrategyAction[keyof StrategyAction] | Partial<StrategyAction['timing']>) => {
    const updatedActions = [...actions];
    
    // Handle nested timing object
    if (field === 'timing' && typeof value === 'object' && value !== null) {
      updatedActions[index] = { 
        ...updatedActions[index], 
        timing: { ...updatedActions[index].timing, ...(value as Partial<StrategyAction['timing']>) }
      };
    } else {
      updatedActions[index] = { ...updatedActions[index], [field]: value };
    }
    
    // Reset template when type changes
    if (field === 'type') {
      updatedActions[index].templateName = '';
    }
    
    onActionsChange(updatedActions);
  };

  const moveStage = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= actions.length) return;

    const updatedActions = [...actions];
    const [movedAction] = updatedActions.splice(fromIndex, 1);
    updatedActions.splice(toIndex, 0, movedAction);

    // Renumber actions
    const renumberedActions = updatedActions.map((action, i) => ({
      ...action,
      order: i + 1,
    }));

    onActionsChange(renumberedActions);
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    moveStage(draggedIndex, index);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const getTemplateOptions = (type: StrategyAction['type']) => {
    switch (type) {
      case 'email':
        return emailTemplateOptions.map((t) => t.name);
      case 'sms':
        return smsTemplates;
      default:
        return [];
    }
  };

  const showTemplateField = (type: StrategyAction['type']) => {
    return type === 'email' || type === 'sms';
  };

  const getActionTypeLabel = (type: StrategyAction['type']): string => {
    const labels: Record<StrategyAction['type'], string> = {
      email: 'Email',
      phone_call: 'Phone Call',
      payment_link: 'Payment Link',
      sms: 'SMS',
      escalation: 'Escalation',
      account_action: 'Place On Stop',
      payment_plan: 'Payment Plan',
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Collection Stages</h3>
          <p className="text-sm text-muted-foreground">
            Define collection actions with timelines and templates
          </p>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={addStage}
                disabled={actions.length >= 10}
                size="sm"
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Stage
              </Button>
            </TooltipTrigger>
            {actions.length >= 10 && (
              <TooltipContent>
                <p>Maximum 10 stages reached</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Stage Cards */}
      <div className="space-y-3">
        {actions.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-8">
              <p className="text-muted-foreground mb-4">No collection stages defined</p>
              <Button onClick={addStage} variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add First Stage
              </Button>
            </CardContent>
          </Card>
        ) : (
          actions
            .sort((a, b) => a.order - b.order)
            .map((action, index) => (
              <Card
                key={action.id}
                className="border hover:border-teal-300 dark:hover:border-teal-700 transition-colors"
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
              >
                <CardContent className="py-4">
                  <div className="flex items-start gap-3">
                    {/* Drag Handle and Stage Badge */}
                    <div className="flex items-center gap-2 flex-shrink-0 pt-2">
                      <div className="cursor-grab active:cursor-grabbing">
                        <GripVertical className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <Badge className="bg-teal-600 text-white whitespace-nowrap">
                        Stage {action.order}
                      </Badge>
                    </div>

                    {/* All Fields in a Single Row */}
                    <div className="flex-1 grid grid-cols-4 gap-3">
                      {/* Action Type */}
                      <div className="space-y-1.5">
                        <Label htmlFor={`actionType-${index}`} className="text-xs">
                          Action Type <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          value={action.type}
                          onValueChange={(value: StrategyAction['type']) => updateStage(index, 'type', value)}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {actionTypeOptions.length > 0 ? (
                              actionTypeOptions
                                .filter((option) => !['payment_link', 'sms', 'payment_plan'].includes(option.code))
                                .map((option) => (
                                  <SelectItem key={option.id} value={option.code}>
                                    {option.name}
                                  </SelectItem>
                                ))
                            ) : (
                              <>
                                <SelectItem value="email">Email</SelectItem>
                                <SelectItem value="phone_call">Phone Call</SelectItem>
                                {/* <SelectItem value="payment_link">Payment Link</SelectItem> */}
                                {/* <SelectItem value="sms">SMS</SelectItem> */}
                                <SelectItem value="escalation">Escalation</SelectItem>
                                <SelectItem value="account_action">Place On Stop</SelectItem>
                                {/* <SelectItem value="payment_plan">Payment Plan</SelectItem> */}
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Timing Type */}
                      <div className="space-y-1.5">
                        <Label htmlFor={`timingType-${index}`} className="text-xs">
                          Timing Type <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          value={action.timing.type}
                                                    onValueChange={(value: 'days_from_trigger' | 'days_from_previous' | 'conditional') => {
                                                      const newTiming = {
                                                        type: value,
                                                        daysFromTrigger: value === 'days_from_trigger' ? (action.timing.daysFromTrigger || 0) : undefined,
                                                        daysFromPrevious: value === 'days_from_previous' ? (action.timing.daysFromPrevious || 1) : undefined,
                                                      };
                                                      updateStage(index, 'timing', newTiming);
                                                    }}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="days_from_trigger">Days from Trigger</SelectItem>
                            <SelectItem value="days_from_previous">Days from Previous</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Days Input (conditional based on timing type) */}
                      <div className="space-y-1.5">
                        {action.timing.type === 'days_from_trigger' && (
                          <>
                            <Label htmlFor={`daysFromTrigger-${index}`} className="text-xs">
                              Days from Trigger <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              id={`daysFromTrigger-${index}`}
                              type="number"
                              min="0"
                              value={action.timing.daysFromTrigger ?? 0}
                              onChange={(e) => {
                                const value = parseInt(e.target.value) || 0;
                                updateStage(index, 'timing', {
                                  ...action.timing,
                                  daysFromTrigger: Math.max(0, value),
                                });
                              }}
                              placeholder="Days"
                              className="h-9"
                            />
                          </>
                        )}
                        {action.timing.type === 'days_from_previous' && (
                          <>
                            <Label htmlFor={`daysFromPrevious-${index}`} className="text-xs">
                              Days from Previous <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              id={`daysFromPrevious-${index}`}
                              type="number"
                              min="1"
                              value={action.timing.daysFromPrevious ?? 1}
                              onChange={(e) => {
                                const value = parseInt(e.target.value) || 1;
                                updateStage(index, 'timing', {
                                  ...action.timing,
                                  daysFromPrevious: Math.max(1, value),
                                });
                              }}
                              placeholder="Days"
                              className="h-9"
                            />
                          </>
                        )}
                        {action.timing.type === 'conditional' && (
                          <>
                            <Label className="text-xs">Conditional</Label>
                            <div className="h-9 flex items-center px-3 border rounded-md bg-muted text-xs text-muted-foreground">
                              Based on conditions
                            </div>
                          </>
                        )}
                      </div>

                      {/* Template Selection (conditional based on action type) */}
                      <div className="space-y-1.5">
                        {showTemplateField(action.type) ? (
                          <>
                            <Label htmlFor={`template-${index}`} className="text-xs">
                              Template <span className="text-red-500">*</span>
                            </Label>
                            <Select
                              value={action.templateName}
                              onValueChange={(value) => updateStage(index, 'templateName', value)}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder="Select template" />
                              </SelectTrigger>
                              <SelectContent>
                                {getTemplateOptions(action.type).map((template) => (
                                  <SelectItem key={template} value={template}>
                                    {template}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </>
                        ) : (
                          <>
                            <Label className="text-xs">Action</Label>
                            <div className="h-9 flex items-center px-3 border rounded-md bg-muted text-xs">
                              {getActionTypeLabel(action.type)}
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Delete Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeStage(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 flex-shrink-0 h-9 w-9 p-0 mt-6"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
        )}
      </div>
    </div>
  );
}
